import React, { createContext, useContext, useState, useEffect, ReactNode } from 'react'
import { adminApi } from '@/lib/api'

interface AdminUser {
  id: string
  name: string
  email: string
  role: string
  emailVerifiedAt: string
  lastLoginAt: string
  createdAt: string
  isAdmin: boolean
}

interface AdminAuthContextType {
  user: AdminUser | null
  login: (email: string, password: string) => Promise<void>
  logout: () => void
  isLoading: boolean
  isAuthenticated: boolean
}

const AdminAuthContext = createContext<AdminAuthContextType | undefined>(undefined)

interface AdminAuthProviderProps {
  children: ReactNode
}

export const AdminAuthProvider: React.FC<AdminAuthProviderProps> = ({ children }) => {
  const [user, setUser] = useState<AdminUser | null>(null)
  const [isLoading, setIsLoading] = useState(true)

  const isAuthenticated = !!user

  // Check for existing admin token on mount
  useEffect(() => {
    const token = localStorage.getItem('admin_token')
    const storedUser = localStorage.getItem('admin_user')
    
    if (token && storedUser) {
      try {
        const parsedUser = JSON.parse(storedUser)
        setUser(parsedUser)
      } catch (error) {
        console.error('Error parsing stored admin user:', error)
        logout()
      }
    }
    
    setIsLoading(false)
  }, [])

  const login = async (email: string, password: string) => {
    try {
      setIsLoading(true)
      
      const response = await adminApi.post('/auth/admin-login', { email, password })
      
      if (response.data.status === 'success') {
        const { user: userData, token } = response.data.data
        
        // Store in localStorage
        localStorage.setItem('admin_token', token)
        localStorage.setItem('admin_user', JSON.stringify(userData))
        
        // Set in state
        setUser(userData)
        
      } else {
        throw new Error(response.data.message || 'Login failed')
      }
    } catch (error: any) {
      console.error('Admin login error:', error)
      throw new Error(error.response?.data?.message || error.message || 'Login failed')
    } finally {
      setIsLoading(false)
    }
  }

  const logout = () => {
    // Clear localStorage
    localStorage.removeItem('admin_token')
    localStorage.removeItem('admin_user')
    
    // Clear state
    setUser(null)
    
  }

  const value = {
    user,
    login,
    logout,
    isLoading,
    isAuthenticated
  }

  return (
    <AdminAuthContext.Provider value={value}>
      {children}
    </AdminAuthContext.Provider>
  )
}

export const useAdminAuth = () => {
  const context = useContext(AdminAuthContext)
  if (context === undefined) {
    throw new Error('useAdminAuth must be used within an AdminAuthProvider')
  }
  return context
}
